import {
  UserProfile,
  SiteData,
  SiteSubscription,
  SiteDomain,
  SiteSettings,
  SitePage,
  PageSection,
  YachtItem,
  YachtReservation,
  YachtAvailability,
  YachtRoute,
  YachtMarina,
  BlogPost,
  MediaAsset,
  IndustryType,
  TemplateDefinition,
  SubscriptionPlanId,
  ShopierTransaction,
} from '../types';
import { realtimeDbService } from './realtimeDbService';

class FirebaseDatabaseService {
  // No automatic mock / demo / fake data creation
  public async initializeSeedData(): Promise<void> {
    // Strictly adheres to: no auto-generated mock, demo, or fake data.
  }

  // User Operations
  public async getUser(uid: string): Promise<UserProfile | null> {
    return realtimeDbService.getUser(uid);
  }

  public async getUsers(): Promise<UserProfile[]> {
    return realtimeDbService.getAllUsers();
  }

  public async getAllUsers(): Promise<UserProfile[]> {
    return realtimeDbService.getAllUsers();
  }

  public async saveUser(user: UserProfile): Promise<UserProfile> {
    return realtimeDbService.saveUser(user);
  }

  // Sites Operations
  public async getSitesByUser(userId: string): Promise<SiteData[]> {
    return realtimeDbService.getSitesByUser(userId);
  }

  public async getSiteById(siteId: string): Promise<SiteData | null> {
    return realtimeDbService.getSite(siteId);
  }

  public async getSiteBySlugOrDomain(slugOrDomain: string): Promise<SiteData | null> {
    return realtimeDbService.getSiteBySlugOrDomain(slugOrDomain);
  }

  public async createSite(
    ownerId: string,
    name: string,
    subdomain: string,
    industry: IndustryType,
    templateId: string,
    planId: SubscriptionPlanId = 'PLAN_CORP_ANNUAL'
  ): Promise<SiteData> {
    return realtimeDbService.createSite(ownerId, name, subdomain, industry, templateId, planId);
  }

  public async updateSite(site: SiteData): Promise<SiteData> {
    return realtimeDbService.updateSite(site);
  }

  public async publishSite(siteId: string): Promise<SiteData> {
    return realtimeDbService.publishSite(siteId);
  }

  public async unpublishSite(siteId: string): Promise<SiteData> {
    return realtimeDbService.unpublishSite(siteId);
  }

  public async deleteSite(siteId: string): Promise<void> {
    return realtimeDbService.deleteSite(siteId);
  }

  // Site Members
  public async getSiteMembers(siteId: string) {
    return realtimeDbService.getSiteMembers(siteId);
  }

  public async addSiteMember(
    siteId: string,
    uid: string,
    role: 'owner' | 'admin' | 'editor' | 'viewer',
    addedBy?: string
  ) {
    return realtimeDbService.saveSiteMember(siteId, uid, role, addedBy);
  }

  public async removeSiteMember(siteId: string, uid: string) {
    return realtimeDbService.removeSiteMember(siteId, uid);
  }

  // Site Pages & Sections
  public async getSitePages(siteId: string): Promise<SitePage[]> {
    return realtimeDbService.getSitePages(siteId);
  }

  public async saveSitePages(siteId: string, pages: SitePage[]): Promise<void> {
    return realtimeDbService.saveSitePages(siteId, pages);
  }

  public async getPageSections(siteId: string, pageId: string): Promise<PageSection[]> {
    return realtimeDbService.getPageSections(siteId, pageId);
  }

  public async savePageSections(siteId: string, pageId: string, sections: PageSection[]): Promise<void> {
    return realtimeDbService.savePageSections(siteId, pageId, sections);
  }

  // Yacht Module
  public async getYachts(siteId: string): Promise<YachtItem[]> {
    return realtimeDbService.getSiteYachts(siteId);
  }

  public async saveYacht(siteId: string, yacht: Partial<YachtItem>): Promise<YachtItem> {
    return realtimeDbService.saveYacht(siteId, yacht);
  }

  public async deleteYacht(siteId: string, yachtId: string): Promise<void> {
    return realtimeDbService.deleteYacht(siteId, yachtId);
  }

  // Yacht Availability Module
  public async getYachtAvailability(siteId: string, yachtId?: string): Promise<YachtAvailability[]> {
    return realtimeDbService.getYachtAvailability(siteId, yachtId);
  }

  public async saveYachtAvailability(
    siteId: string,
    availability: Partial<YachtAvailability>
  ): Promise<YachtAvailability> {
    return realtimeDbService.saveYachtAvailability(siteId, availability);
  }

  public async deleteYachtAvailability(siteId: string, availabilityId: string): Promise<void> {
    return realtimeDbService.deleteYachtAvailability(siteId, availabilityId);
  }

  public async checkYachtAvailability(
    siteId: string,
    yachtId: string,
    startDate: string,
    endDate: string,
    excludeReservationId?: string
  ): Promise<{ available: boolean; reason?: string }> {
    return realtimeDbService.checkYachtAvailability(siteId, yachtId, startDate, endDate, excludeReservationId);
  }

  // Reservations Module
  public async getReservations(siteId: string): Promise<YachtReservation[]> {
    return realtimeDbService.getSiteReservations(siteId);
  }

  public async createReservationInquiry(
    siteId: string,
    inquiry: Omit<YachtReservation, 'id' | 'createdAt' | 'status'>
  ): Promise<YachtReservation> {
    return realtimeDbService.createReservationInquiry(siteId, inquiry);
  }

  public async updateReservationStatus(
    siteId: string,
    reservationId: string,
    status: YachtReservation['status'],
    adminNotes?: string
  ): Promise<void> {
    return realtimeDbService.updateReservationStatus(siteId, reservationId, status, adminNotes);
  }

  // Routes Module (Yacht Rental Vertical)
  public async getRoutes(siteId: string): Promise<YachtRoute[]> {
    return realtimeDbService.getSiteRoutes(siteId);
  }

  public async saveRoute(siteId: string, route: Partial<YachtRoute>): Promise<YachtRoute> {
    return realtimeDbService.saveRoute(siteId, route);
  }

  public async deleteRoute(siteId: string, routeId: string): Promise<void> {
    return realtimeDbService.deleteRoute(siteId, routeId);
  }

  // Marinas Module (Yacht Rental Vertical)
  public async getMarinas(siteId: string): Promise<YachtMarina[]> {
    return realtimeDbService.getSiteMarinas(siteId);
  }

  public async saveMarina(siteId: string, marina: Partial<YachtMarina>): Promise<YachtMarina> {
    return realtimeDbService.saveMarina(siteId, marina);
  }

  public async deleteMarina(siteId: string, marinaId: string): Promise<void> {
    return realtimeDbService.deleteMarina(siteId, marinaId);
  }

  // Blog Module
  public async getBlogPosts(siteId: string, onlyPublished: boolean = false): Promise<BlogPost[]> {
    return realtimeDbService.getSiteBlogs(siteId, onlyPublished);
  }

  public async saveBlogPost(siteId: string, post: Partial<BlogPost>): Promise<BlogPost> {
    return realtimeDbService.saveBlogPost(siteId, post);
  }

  public async deleteBlogPost(siteId: string, postId: string): Promise<void> {
    return realtimeDbService.deleteBlogPost(siteId, postId);
  }

  // Media Module
  public async getMedia(siteId: string): Promise<MediaAsset[]> {
    return realtimeDbService.getSiteMedia(siteId);
  }

  public async uploadMedia(siteId: string, asset: Omit<MediaAsset, 'id' | 'siteId' | 'uploadedAt'>): Promise<MediaAsset> {
    return realtimeDbService.addMediaAsset(siteId, {
      ...asset,
      siteId,
    });
  }

  public async deleteMedia(siteId: string, mediaId: string): Promise<void> {
    return realtimeDbService.deleteMediaAsset(siteId, mediaId);
  }

  // Domains & Subscriptions
  public async getSiteDomains(siteId: string): Promise<SiteDomain[]> {
    return realtimeDbService.getSiteDomains(siteId);
  }

  public async saveSiteDomains(siteId: string, domains: SiteDomain[]): Promise<void> {
    return realtimeDbService.saveSiteDomains(siteId, domains);
  }

  public async getSiteSubscription(siteId: string): Promise<SiteSubscription | null> {
    return realtimeDbService.getSiteSubscription(siteId);
  }

  public async saveSiteSubscription(siteId: string, sub: SiteSubscription): Promise<void> {
    return realtimeDbService.saveSiteSubscription(siteId, sub);
  }

  public async saveSite(site: SiteData): Promise<SiteData> {
    return realtimeDbService.saveSite(site);
  }

  public async getPaymentTransactions(): Promise<ShopierTransaction[]> {
    return realtimeDbService.getPaymentTransactions();
  }

  public async savePaymentTransaction(tx: ShopierTransaction): Promise<void> {
    return realtimeDbService.savePaymentTransaction(tx);
  }

  public async saveReservation(siteId: string, reservation: Partial<YachtReservation>): Promise<YachtReservation> {
    return realtimeDbService.saveReservation(siteId, reservation);
  }

  public async deleteReservation(siteId: string, reservationId: string): Promise<void> {
    return realtimeDbService.deleteReservation(siteId, reservationId);
  }

  // Analytics
  public async getSiteAnalytics(siteId: string) {
    return realtimeDbService.getSiteAnalytics(siteId);
  }
}

export const dbService = new FirebaseDatabaseService();
dbService.initializeSeedData();
export default dbService;
